// Реестр стран, для которых существует интерактивная карта (/<cc>/map).
//
// Единственная точка правды: на него смотрят guard роутов, загрузчики данных,
// choropleth (пороги зума по уровням), build-sitemap.mjs.
//
// Добавление страны — docs/maps/ADD_COUNTRY_MAP.md. Здесь описывается только то,
// что фронтенду нужно ЗНАТЬ ДО загрузки meta.json: где лежат файлы и как называются
// уровни. Всё остальное (center, zoom, bbox, валюта, локаль, термины) приходит
// из meta.json страны и здесь не дублируется.

import type { TopoLevel } from '@/data/types';

export const MAP_COUNTRIES = ['it', 'es', 'pt'] as const;

export type MapCountry = (typeof MAP_COUNTRIES)[number];

export const DEFAULT_MAP_COUNTRY: MapCountry = 'it';

/** Уровень админ-иерархии: с какого зума он становится активным слоем choropleth. */
export interface MapAdminLevel {
  /** Ключ уровня — он же имя объекта внутри TopoJSON и значение ChoroplethView. */
  key: TopoLevel;
  /** 1 = самый крупный уровень. */
  order: number;
  /** Файл в каталоге данных страны. */
  topoFile: string;
  /** С какого зума уровень активен. Должен строго возрастать вместе с order. */
  zoomFrom: number;
  /**
   * Имя свойства внутри TopoJSON, в котором лежит код единицы — тот самый,
   * по которому полигон находит свою запись в датасете.
   *
   * Он обязан быть здесь, а не в коде: у Италии это `com_istat_code`, у
   * Испании — `ine`, и никакой общей нормы не существует. Пока имя было
   * зашито в data/normalize.ts, испанские полигоны получали код `null` —
   * и это не падало, а тихо давало карту без заливки, без тултипов и без
   * панели деталей: сборка зелёная, файлы на месте, ошибок в консоли нет.
   */
  codeProp: string;
  /** Имя свойства с человеческим названием единицы, если оно есть в TopoJSON. */
  nameProp?: string;
}

export interface MapCountryConfig {
  cc: MapCountry;
  /**
   * Название страны для головных тегов маршрута карты.
   *
   * Оно есть и в countries.json — но оттуда приходит асинхронно, а пререндер
   * снимает HTML по domcontentloaded. Проигранная гонка не роняет страницу и не
   * портит canonical: заголовок просто становится «ES relocation map» вместо
   * «Spain relocation map». Неверно только слово, и заметит это лишь читатель.
   *
   * Это осознанный дубль, а не второй источник правды: countries.json остаётся
   * источником для всего остального, а совпадение этих двух полей с name_en /
   * name_ru проверяется в registry-sync.test.ts — расхождение падает в CI на
   * первом же прогоне.
   */
  nameEn: string;
  nameRu: string;
  /**
   * Подкаталог внутри DATA_BASE_URL. Пустая строка = корень /data.
   *
   * Италия исторически лежит в корне, и переносить 16 МБ данных в /data/it/
   * ради симметрии — риск без выгоды: сломается симлинк web/public/data,
   * rsync в web-deploy.yml и все задеплоенные пути. Новые страны получают
   * собственный подкаталог, изоляция достигается тем же.
   */
  dataDir: string;
  /** Полный датасет базовых единиц. */
  fullFile: string;
  /** Урезанный датасет для первой загрузки карты. */
  lightFile: string;
  /** Справочник имён промежуточного уровня; null — если уровня нет. */
  districtsMetaFile: string | null;
  /** Админ-уровни от крупного к мелкому. */
  levels: MapAdminLevel[];
  /** [west, south, east, north] — отсечка заведомо чужих координат (пины, импорт). */
  bbox: [number, number, number, number];
  /**
   * Языковой раздел Википедии, откуда берётся фото и ссылка «читать дальше»
   * в панели деталей. Итальянская коммуна описана в it.wikipedia, испанский
   * муниципалитет — в es.wikipedia; статьи о малых населённых пунктах в чужом
   * разделе чаще всего просто отсутствуют.
   */
  wikiLang: string;
}

const IT: MapCountryConfig = {
  cc: 'it',
  nameEn: 'Italy',
  nameRu: 'Италия',
  dataDir: '',
  fullFile: 'comuni.json',
  lightFile: 'comuni_light.json',
  districtsMetaFile: 'districts_it.json',
  levels: [
    { key: 'regions', order: 1, topoFile: 'regions_it.topojson', zoomFrom: 0, codeProp: 'reg_istat_code', nameProp: 'reg_name' },
    { key: 'provinces', order: 2, topoFile: 'provinces_it.topojson', zoomFrom: 7, codeProp: 'prov_acr', nameProp: 'prov_name' },
    { key: 'districts', order: 3, topoFile: 'districts_it.topojson', zoomFrom: 9, codeProp: 'dist_code', nameProp: 'dist_name_it' },
    { key: 'comuni', order: 4, topoFile: 'comuni_it.topojson', zoomFrom: 11, codeProp: 'com_istat_code', nameProp: 'name' },
  ],
  bbox: [6, 36, 19, 48],
  wikiLang: 'it',
};

const ES: MapCountryConfig = {
  cc: 'es',
  nameEn: 'Spain',
  nameRu: 'Испания',
  dataDir: 'es',
  fullFile: 'units.json',
  lightFile: 'units_light.json',
  // Промежуточный уровень — comarcas agrarias (318 единиц, медиана 6 на
  // провинцию). Без него карта прыгала с 50 провинций сразу на 8044
  // муниципалитета: медианный испанский муниципалитет — 510 жителей, и на
  // зуме 9 страна распадалась на мозаику, по которой ничего не прочесть.
  districtsMetaFile: 'districts.json',
  // Ключ уровня — это РОЛЬ, а не название на языке страны: по нему фронтенд
  // находит объект внутри TopoJSON и выбирает слой по зуму. Роли достались
  // от первой страны и потому звучат по-итальянски; человеческие названия
  // («автономное сообщество», «муниципалитет») приходят из meta.admin_terms.
  // Канарские острова в датасет не входят — см. pipeline/countries/es/decisions.md D1.
  levels: [
    { key: 'regions', order: 1, topoFile: 'ccaa.topojson', zoomFrom: 0, codeProp: 'codauto', nameProp: 'name' },
    { key: 'provinces', order: 2, topoFile: 'provinces.topojson', zoomFrom: 7, codeProp: 'cpro', nameProp: 'name' },
    { key: 'districts', order: 3, topoFile: 'districts.topojson', zoomFrom: 9, codeProp: 'dist_code', nameProp: 'dist_name' },
    { key: 'comuni', order: 4, topoFile: 'municipalities.topojson', zoomFrom: 11, codeProp: 'ine', nameProp: 'name' },
  ],
  bbox: [-9.6, 35, 4.4, 43.9],
  wikiLang: 'es',
};

const PT: MapCountryConfig = {
  cc: 'pt',
  nameEn: 'Portugal',
  nameRu: 'Португалия',
  dataDir: 'pt',
  fullFile: 'units.json',
  lightFile: 'units_light.json',
  // Промежуточный уровень — concelhos (308 муниципалитетов). Без него карта
  // прыгала бы с 26 субрегионов NUTS 3 сразу на 3259 фрегезий.
  districtsMetaFile: 'districts.json',
  // Роли уровней те же, что и у остальных стран, а вот административная
  // лестница португальская: NUTS 2 → NUTS 3 → concelho → freguesia. Коды
  // уровней 3 и 4 (dtmn, dtmnfr) — строки со значащими ведущими нулями.
  // Верхний уровень начинается с зума 5, а не 0: bbox страны включает Азоры
  // и Мадейру, и на меньшем зуме карта всё равно не показывается
  // (meta.min_zoom = 5).
  levels: [
    { key: 'regions', order: 1, topoFile: 'nuts2.topojson', zoomFrom: 5, codeProp: 'nuts2_code', nameProp: 'nuts2_name' },
    { key: 'provinces', order: 2, topoFile: 'nuts3.topojson', zoomFrom: 7, codeProp: 'nuts3_code', nameProp: 'nuts3_name' },
    { key: 'districts', order: 3, topoFile: 'concelhos.topojson', zoomFrom: 9, codeProp: 'dtmn', nameProp: 'dist_name' },
    { key: 'comuni', order: 4, topoFile: 'freguesias.topojson', zoomFrom: 11, codeProp: 'dtmnfr', nameProp: 'name' },
  ],
  // Западная граница уезжает до -31.3: Азорский архипелаг обязан попадать
  // в bbox, потому что bbox работает барьером карты и отсечкой координат —
  // см. pipeline/countries/pt/decisions.md D-PT-1.
  bbox: [-31.3, 32.4, -6.19, 42.15],
  wikiLang: 'pt',
};

export const MAP_COUNTRY_CONFIG: Record<MapCountry, MapCountryConfig> = { it: IT, es: ES, pt: PT };

export function isMapCountry(value: string | undefined | null): value is MapCountry {
  return !!value && (MAP_COUNTRIES as readonly string[]).includes(value);
}

export function mapCountryConfig(cc: MapCountry = DEFAULT_MAP_COUNTRY): MapCountryConfig {
  return MAP_COUNTRY_CONFIG[cc];
}

/**
 * Путь к интерактивной карте страны, или null, если карты у неё нет.
 *
 * Единственный способ узнать это. Раньше признак жил ещё и в countries.json
 * (`map_url`), то есть два списка описывали одно и то же — ровно та конструкция,
 * из-за которой в этом репозитории уже расходились реестры. Здесь список один:
 * страна есть в MAP_COUNTRIES ⇒ карта есть, и путь выводится, а не хранится.
 *
 * Принимает и ISO-код в любом регистре ('ES', 'es'), потому что зовут её и из
 * каталога стран, где коды заглавные, и из карты, где строчные.
 */
export function mapPathFor(code: string | undefined | null): string | null {
  const cc = code?.toLowerCase();
  return isMapCountry(cc) ? `/${cc}/map` : null;
}

/**
 * Описание уровня по его роли. Возвращает undefined, если у страны такого
 * уровня нет — вызывающий обязан это проверить, а не полагаться на набор
 * уровней первой страны.
 */
export function mapLevel(cfg: MapCountryConfig, key: TopoLevel): MapAdminLevel | undefined {
  return cfg.levels.find((l) => l.key === key);
}

/** Путь к файлу данных страны относительно DATA_BASE_URL. */
export function countryDataPath(cfg: MapCountryConfig, file: string): string {
  return cfg.dataDir ? `${cfg.dataDir}/${file}` : file;
}
